import i18n from "@/locales/i18n";
export const DEFAULT_TEXT_TO_IMAGE_CONFIG = {
  title: i18n.global.t("views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.7a57b713e30c911d3e3e32148c2ccfa6"),
  model: '',
  aspect_ratio: '1:1',
  image_size: '',
  n: 1,
  prompt_template: '',
  reference_images: [],
  output_mode: 'image',
  variable_assign: '',
  exception_handling: {
    enabled: false,
    execution_timeout_seconds: 60,
    first_token_timeout_enabled: false,
    first_token_timeout_seconds: 5,
    retry_times: 0,
    strategy: 'interrupt',
    default_value: {},
    exception_branch_handle_id: ''
  }
};
const DEFAULT_ASPECT_RATIOS = ['1:1', '3:4', '4:3', '9:16', '16:9', '2:3', '3:2'];
export const MAX_REFERENCE_IMAGES = 10;
const MANUAL_SIZE_REG = /^\d+x\d+$/i;
const OUTPUT_MODES = ['image', 'url'];
const REFERENCE_SOURCE_TYPES = ['local', 'variable', 'url'];
const getPlainObject = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value;
};
const dropUndefinedValues = value =>
  Object.fromEntries(
    Object.entries(getPlainObject(value)).filter(
      ([, item]) => typeof item !== 'undefined'
    )
  );
const parseMaybeArray = value => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return value.split(',').map(item => item.trim()).filter(Boolean);
  }
};
const pickList = response => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data?.list)) return response.data.list;
  if (Array.isArray(response?.data?.results)) return response.data.results;
  if (Array.isArray(response?.data?.data?.list)) return response.data.data.list;
  if (Array.isArray(response?.data?.data?.results)) return response.data.data.results;
  if (Array.isArray(response?.results)) return response.results;
  return [];
};
const toPositiveInteger = (value, defaultValue = 1) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue < 1) return defaultValue;
  return Math.floor(numberValue);
};
const toOptionalPositiveInteger = value => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue < 1) return undefined;
  return Math.floor(numberValue);
};
export const clampGeneratedImageCount = (value, maxImages) => {
  const imageCount = toPositiveInteger(value, 1);
  const resolvedMaxImages = toOptionalPositiveInteger(maxImages);
  return resolvedMaxImages
    ? Math.min(imageCount, resolvedMaxImages)
    : imageCount;
};
const isReferenceImageUrl = value => {
  try {
    const url = new URL(String(value || '').trim());
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
};
export const parseReferenceImageUrls = value => {
  return String(value || '').split(/\r\n|\n|\r/).map(item => item.trim()).filter(isReferenceImageUrl);
};
export const normalizeImageModelListResponse = response => {
  return pickList(response).filter(item => item && item.is_active !== 0 && item.is_delete !== 1).map(item => {
    const aspectRatios = parseMaybeArray(item.aspect_ratios);
    const modelName = item.model_name ? String(item.model_name) : '';
    const displayName = item.model_display_name ? String(item.model_display_name) : modelName;
    return {
      ...item,
      label: displayName,
      value: modelName,
      model_display_name: displayName,
      model_name: modelName,
      support_reference_image: Number(item.support_reference_image ?? 1),
      aspect_ratios: aspectRatios.length ? aspectRatios : DEFAULT_ASPECT_RATIOS,
      max_images: toOptionalPositiveInteger(item.max_images),
      prompt_max_length: toPositiveInteger(item.prompt_max_length, 2000)
    };
  }).filter(item => item.value);
};
export const normalizeAspectRatioFields = value => {
  const aspectRatio = value || '1:1';
  return {
    aspect_ratio: aspectRatio,
    image_size: MANUAL_SIZE_REG.test(aspectRatio) ? aspectRatio : ''
  };
};
export const normalizeReferenceImages = (list, {
  maxImages = Infinity,
  supportReferenceImage = true
} = {}) => {
  if (!supportReferenceImage) return [];
  return (Array.isArray(list) ? list : []).filter(item => item?.source_type && item?.value).slice(0, maxImages).map((item, index) => ({
    source_type: item.source_type,
    value: item.value,
    ...(item.source_type === 'local' && item.permanent ? {
      permanent: item.permanent
    } : {}),
    order: index
  }));
};
const buildConfigurationSnapshot = config => {
  const configuration = {
    ...getPlainObject(config?.configuration)
  };
  delete configuration.exception_config;
  delete configuration.exception_handling;
  return {
    ...configuration,
    title: config.title,
    model: config.model,
    aspect_ratio: config.aspect_ratio,
    image_size: config.image_size,
    n: config.n,
    prompt_template: config.prompt_template,
    reference_images: config.reference_images,
    output_mode: config.output_mode,
    variable_assign: config.variable_assign
  };
};
export const ensureTextToImageConfig = config => {
  const configuration = getPlainObject(config?.configuration);
  const legacyExceptionConfig =
    config?.exception_config || configuration.exception_config;
  const exceptionHandling =
    config?.exception_handling || configuration.exception_handling;
  const normalized = {
    ...DEFAULT_TEXT_TO_IMAGE_CONFIG,
    ...dropUndefinedValues(configuration),
    ...dropUndefinedValues(config),
    reference_images: Array.isArray(config?.reference_images)
      ? config.reference_images
      : Array.isArray(configuration.reference_images)
        ? configuration.reference_images
        : []
  };
  if (exceptionHandling) {
    normalized.exception_handling = {
      ...DEFAULT_TEXT_TO_IMAGE_CONFIG.exception_handling,
      ...dropUndefinedValues(exceptionHandling)
    };
    delete normalized.exception_config;
  } else if (legacyExceptionConfig) {
    delete normalized.exception_handling;
    normalized.exception_config = legacyExceptionConfig;
  } else {
    delete normalized.exception_config;
    normalized.exception_handling = {
      ...DEFAULT_TEXT_TO_IMAGE_CONFIG.exception_handling
    };
  }
  return normalized;
};
export const validateTextToImageConfig = (config, model) => {
  const errors = [];
  const normalized = ensureTextToImageConfig(config);
  const hasModelDetail = Boolean(model);
  if (!normalized.model) {
    errors.push(i18n.global.t("views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.4f176ae96220bb49c882e4b0783eb04e"));
  }
  if (!String(normalized.prompt_template || '').trim()) {
    errors.push(i18n.global.t("views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.7ee918106208da0072babd3fd5f1b918"));
  }
  if (!String(normalized.variable_assign || '').trim()) {
    errors.push(i18n.global.t("views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.804556b6897d21595c14bfa2fc2b29c2"));
  }
  if (!OUTPUT_MODES.includes(normalized.output_mode)) {
    errors.push(i18n.global.t("views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.79e89c4bc7dcedaf9103428c5dba992e"));
  }
  const maxImages = toOptionalPositiveInteger(model?.max_images);
  if (
    hasModelDetail &&
    maxImages &&
    (normalized.n < 1 || normalized.n > maxImages)
  ) {
    errors.push(i18n.global.t('views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.67a3a885a8cf5330b624a9196b4978c9', [maxImages]));
  }
  const promptLimit = Number(model?.prompt_max_length || 2000);
  if (hasModelDetail && String(normalized.prompt_template || '').length > promptLimit) {
    errors.push(i18n.global.t('views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.11c016fbd362683be4ccb3f108d574b9', [promptLimit]));
  }
  const aspectOptions = Array.isArray(model?.aspect_ratios) ? model.aspect_ratios : DEFAULT_ASPECT_RATIOS;
  if (hasModelDetail && normalized.aspect_ratio && !aspectOptions.includes(normalized.aspect_ratio)) {
    errors.push(i18n.global.t("views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.ccefa803c6e9cb2b6f1578daac0c2d36"));
  }
  if (hasModelDetail && normalized.reference_images?.length > MAX_REFERENCE_IMAGES) {
    errors.push(i18n.global.t('views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.34773477c3e4c3304f94c25870dbb714', [MAX_REFERENCE_IMAGES]));
  }
  normalized.reference_images.forEach((item, index) => {
    if (!REFERENCE_SOURCE_TYPES.includes(item?.source_type)) return;
    if (Number(item.order) !== index) {
      errors.push(i18n.global.t("views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.09ba79208f990dd0616e7001559cc7fb"));
    }
    if (item.source_type === 'local' && !item.permanent) {
      errors.push(i18n.global.t("views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.87a3a2e5524732a047bcb58d90866409"));
    }
    if (item.source_type === 'url' && !isReferenceImageUrl(item.value)) {
      errors.push(i18n.global.t("views.flow.FlowGrap.CustomNode.BlockNodeChild.NodeInfo.textToImageHelper.f9928867c18e8c4d9bc13f76f0775b47"));
    }
  });
  return errors;
};
export const buildTextToImageSubmitConfig = (config, model) => {
  const normalized = ensureTextToImageConfig(config);
  const aspectFields = normalizeAspectRatioFields(normalized.aspect_ratio);
  const supportReferenceImage = Number(model?.support_reference_image ?? 1) === 1;
  const maxImages = toOptionalPositiveInteger(model?.max_images);
  const submitConfig = {
    ...normalized,
    ...aspectFields,
    n: clampGeneratedImageCount(normalized.n, maxImages),
    reference_images: normalizeReferenceImages(normalized.reference_images, {
      maxImages: MAX_REFERENCE_IMAGES,
      supportReferenceImage
    })
  };
  return {
    ...submitConfig,
    configuration: buildConfigurationSnapshot(submitConfig)
  };
};
